/**
 * Pricing card. 4 tiers (Free, Pro, Family, Empire Bundle).
 * Razorpay checkout via lazy chassis/payment.ts.
 */
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { PRICING, openCheckout, currentEntitlement } from '../chassis/payment.ts';
import type { Tier } from '../chassis/payment.ts';
import { store } from '../store.ts';

const TPL = /* html */ `
<style>
.tier-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
  margin-top: 14px;
}
.tier {
  padding: 16px;
  background: var(--parchment);
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  display: grid;
  gap: 8px;
  position: relative;
  transition: transform 200ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 200ms;
}
.tier:hover { transform: translateY(-1px); box-shadow: 0 8px 24px -12px var(--indigo-wash); }
.tier.active {
  border-color: var(--indigo);
  background: var(--indigo-wash);
}
.tier-head {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: 10px;
}
.tier-name {
  font-family: var(--display);
  font-weight: 600;
  font-size: 22px;
  color: var(--indigo);
}
.tier-deva {
  font-family: var(--hindi);
  color: var(--ink-dim);
  font-size: 16px;
}
.tier-price {
  font-family: var(--mono);
  font-size: 14px;
  color: var(--ink);
  letter-spacing: 0.05em;
}
.tier-pitch {
  font-family: var(--display);
  font-style: italic;
  color: var(--ink-dim);
  font-size: 14px;
  line-height: 1.4;
}
.tier-perks {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 4px;
}
.tier-perks li {
  font-family: var(--display);
  font-size: 13.5px;
  color: var(--ink);
  padding-left: 18px;
  position: relative;
  line-height: 1.4;
}
.tier-perks li::before {
  content: '∴';
  position: absolute;
  left: 0;
  color: var(--indigo);
}
.toggle-row {
  display: inline-flex;
  border: 1px solid var(--rule-strong);
  border-radius: 99px;
  overflow: hidden;
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}
.toggle-row button {
  padding: 6px 14px;
  background: transparent;
  color: var(--ink-dim);
  cursor: pointer;
  border: 0;
}
.toggle-row button.on {
  background: var(--indigo);
  color: var(--parchment);
}
@container (min-width: 560px) {
  .tier-grid { grid-template-columns: 1fr 1fr; }
}
</style>
<section class="frame">
  <h2>Pricing <span class="deva">मूल्य</span></h2>
  <div class="kicker">All tiers · annual gives ~2 months off</div>
  <div style="display:flex;gap:8px;align-items:center;">
    <span class="kicker" style="margin:0;">billing</span>
    <div class="toggle-row" id="toggle">
      <button data-b="monthly">Monthly</button>
      <button data-b="yearly" class="on">Annual</button>
    </div>
  </div>
  <div class="tier-grid" id="tiers"></div>
  <div style="margin-top:18px;font-family:var(--mono);font-size:11px;letter-spacing:0.08em;color:var(--ink-faint);text-align:center;padding-top:14px;border-top:1px dashed var(--rule)">
    Razorpay live · 30-day money-back · plaintext never reaches our servers · DPDP 2023 · <a href="mailto:dpo@taxwalaai.com" style="color:inherit;text-decoration:underline">dpo@taxwalaai.com</a>
  </div>
</section>
`;

export class WasiyatPricingCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #billing: 'monthly' | 'yearly' = 'yearly';

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, () => this.#paint());

    const toggle = this.#shadow.getElementById('toggle');
    toggle?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.tagName !== 'BUTTON') return;
      const b = t.dataset['b'] as 'monthly' | 'yearly';
      if (!b) return;
      this.#billing = b;
      toggle.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === t));
      this.#paint();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  #paint(): void {
    const mount = this.#shadow.getElementById('tiers');
    if (!mount) return;
    const ent = currentEntitlement();
    mount.innerHTML = PRICING.map((p) => {
      const amt = this.#billing === 'monthly' ? p.monthly_paise : p.yearly_paise;
      const label = p.id === 'free' ? 'Free forever' : `₹${(amt / 100).toLocaleString('en-IN')}/${this.#billing === 'monthly' ? 'mo' : 'yr'}`;
      const active = ent.tier === p.id ? 'active' : '';
      return `<div class="tier ${active}" data-tier="${p.id}">
        <div class="tier-head">
          <span class="tier-name">${escapeHtml(p.name)}</span>
          <span class="tier-deva">${escapeHtml(p.hindi)}</span>
          <span class="tier-price">${label}</span>
        </div>
        <div class="tier-pitch">${escapeHtml(p.pitch)}</div>
        <ul class="tier-perks">${p.perks.map((perk) => `<li>${escapeHtml(perk)}</li>`).join('')}</ul>
        <button class="btn tiny" data-buy="${p.id}" ${active ? 'disabled' : ''}>${active ? 'Current tier' : (p.id === 'free' ? 'Use free' : 'Upgrade')}</button>
      </div>`;
    }).join('');
    mount.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset['buy'] as Tier;
        const ownerName = store.state.vault?.owner.display_name ?? '';
        const ok = await openCheckout(tier, this.#billing, ownerName);
        if (ok) this.#paint();
      });
    });
  }
}

customElements.define('wasiyat-pricing-card', WasiyatPricingCard);
